import React from "react";
import { format, parseISO } from "date-fns";
import { Paperclip, CheckCircle2 } from "lucide-react";

export const TICKET_CATEGORIES = {
  new_page: { label: "New Page", color: "#EF4444" },
  new_content: { label: "New Content", color: "#0066ff" },
  edit_content: { label: "Edit Content", color: "#10B981" },
  bug_fix: { label: "Bug Fix", color: "#F59E0B" },
  other: { label: "Other", color: "#71717A" },
};

export const PRIORITY_COLORS = {
  1: "#10B981",  // green
  2: "#F59E0B",  // yellow
  3: "#FB923C",  // orange
  4: "#EF4444",  // red
  5: "#7F1D1D",  // maroon
};

// Priority "!" string with growing size; 4 and 5 are larger
export function PriorityBangs({ level }) {
  const n = Math.max(1, Math.min(5, level || 1));
  const color = PRIORITY_COLORS[n];
  // size: 1-3 = base; 4 = bigger; 5 = even bigger
  const sizeCls = n >= 5 ? "text-xl" : n === 4 ? "text-lg" : "text-sm";
  const weightCls = n >= 4 ? "font-extrabold" : "font-bold";
  return (
    <span
      data-testid={`priority-${n}`}
      className={`inline-flex items-center leading-none tracking-tight ${sizeCls} ${weightCls}`}
      style={{ color }}
      title={`Priority ${n}`}
    >
      {"!".repeat(n)}
    </span>
  );
}

export function CategoryPill({ category }) {
  const c = TICKET_CATEGORIES[category] || TICKET_CATEGORIES.other;
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold"
      style={{ backgroundColor: `${c.color}1A`, color: c.color }}
    >
      {c.label}
    </span>
  );
}

export function TicketCard({ ticket, onClick, hideClient = true, clientName }) {
  const isComplete = ticket.status === "complete";
  const cat = TICKET_CATEGORIES[ticket.category] || TICKET_CATEGORIES.other;
  return (
    <div
      data-testid={`ticket-card-${ticket.id}`}
      onClick={onClick}
      className={`group relative cursor-pointer rounded-xl border bg-white p-4 transition-all hover:-translate-y-[1px] hover:shadow-md ${
        isComplete ? "border-zinc-200 opacity-60 grayscale" : "border-zinc-200"
      }`}
      style={{ borderLeft: `4px solid ${cat.color}` }}
    >
      {isComplete && (
        <div className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-md bg-emerald-500 shadow-sm">
          <CheckCircle2 size={14} className="text-white" strokeWidth={2.5} />
        </div>
      )}
      <div className="flex items-start justify-between gap-2 pr-6">
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold text-zinc-900">{ticket.title}</div>
          {ticket.page_name && (
            <div className="mt-0.5 truncate text-xs text-zinc-500">on <span className="font-medium text-zinc-700">{ticket.page_name}</span></div>
          )}
        </div>
        <PriorityBangs level={ticket.priority} />
      </div>
      {ticket.description && (
        <div className="mt-2 line-clamp-2 text-xs text-zinc-600">{ticket.description}</div>
      )}
      <div className="mt-3 flex items-center justify-between gap-2">
        <CategoryPill category={ticket.category} />
        <div className="flex items-center gap-2 text-[10px] text-zinc-400">
          {ticket.attachment_file_id && <Paperclip size={11} />}
          {ticket.notes?.length > 0 && <span>{ticket.notes.length} note{ticket.notes.length > 1 ? "s" : ""}</span>}
          <span>{ticket.created_at ? format(parseISO(ticket.created_at), "MMM d") : ""}</span>
        </div>
      </div>
      {!hideClient && clientName && (
        <div className="mt-2 truncate text-[11px] text-zinc-500">{clientName}</div>
      )}
      {typeof ticket.price_amount === "number" && (
        <div className="mt-2 text-[11px] font-medium text-zinc-700">
          {ticket.price_amount === 0 ? "No cost" : `$${ticket.price_amount.toFixed(2)}`}
        </div>
      )}
    </div>
  );
}
